import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Form, Formik, useFormikContext } from 'formik';
import React, { Fragment, useContext } from 'react';
import * as Yup from 'yup';
import { LanguageString } from '..';
import { SiteContext } from '../../contexts';
import { Roadmap, SubmitRoadmapInput } from '../../dto';
import { DialogController, useSubmitRoadmap } from '../../hooks';
import { Checkbox, DateField, DialogTitle, TextField } from '../Common';

type FormType = SubmitRoadmapInput & { isNextMeetingSet?: boolean };

const finalizeRoadmapSchema = (userPinEnabled: boolean, nextMeetingEnabled?: boolean) =>
  Yup.object().shape({
    isNextMeetingSet: nextMeetingEnabled === true ? Yup.boolean().required('Please confirm next meeting has been set') : Yup.boolean(),
    advisorPin: Yup.string().required('PIN is required'),
    studentPin: userPinEnabled ? Yup.string().required('PIN is required') : Yup.string()
  });

type FinalizeRoadmapFormProps = {
  userPinEnabled: boolean;
  endDateEnabled: boolean;
  nextMeetingEnabled?: boolean;
  nextDateEnabled?: boolean;
  reviewNotesEnabled?: boolean;
};

const FinalizeRoadmapForm: React.FC<FinalizeRoadmapFormProps> = ({ userPinEnabled, endDateEnabled, nextDateEnabled, nextMeetingEnabled, reviewNotesEnabled }) => (
  <Grid container spacing={2}>
    {nextMeetingEnabled && <Checkbox item xs={12} name="isNextMeetingSet" label={<LanguageString groupName="ROADMAP" resourceName="ROADMAP_SET_MEETING" />} />}
    {nextDateEnabled && <DateField item xs={12} name="nextRoadmap.plannedStartDate" label={<LanguageString groupName="ROADMAP" resourceName="ROADMAP_START_DATE" />} />}
    {endDateEnabled && <DateField item xs={12} name="nextRoadmap.plannedEndDate" label={<LanguageString groupName="ROADMAP" resourceName="ROADMAP_END_DATE" />} />}
    {reviewNotesEnabled && <TextField id="finalize_reviewNotes" item xs={12} name="reviewNotes" label={<LanguageString groupName="ROADMAP" resourceName="ROADMAP_NOTES" />} rows={5} />}
    <TextField id="finalize_advisorNotes" item xs={12} name="advisorReviewNotes" label={<LanguageString groupName="ROADMAP" resourceName="ROADMAP_COACH_NOTES" />} rows={5} />
    <TextField id="finalize_advisorPin" item xs={12} md={6} name="advisorPin" label={<LanguageString groupName="ROADMAP" resourceName="COACH_PIN" />} type="password" />
    {userPinEnabled && <TextField id="finalize_studentPin" item xs={12} md={6} name="studentPin" label={<LanguageString groupName="ROADMAP" resourceName="STUDENT_PIN" />} type="password" />}
  </Grid>
);

const SubmitBtn: React.FC = () => {
  const formik = useFormikContext();

  return <Button onClick={formik.submitForm}>Submit</Button>;
};

type FinalizeRoadmapProps = { roadmap: Roadmap; dlgCtrl: DialogController };

// const USER_PIN_ENABLED = 1;
const END_DATE_ENABLED = 2;
const NEXT_MEETING_ENABLED = 4;
const NEXT_DATE_ENABLED = 8;
const REVIEW_NOTES_ENABLED = 16;

export const FinalizeRoadmap: React.FC<FinalizeRoadmapProps> = ({ roadmap, dlgCtrl: { props: dlgProps, controller: dlgCtrl } }) => {
  const [submitRoadmap, qry] = useSubmitRoadmap();
  const siteCtx = useContext(SiteContext);
  const userPinEnabled = siteCtx.site?.enableUserPIN === true;

  const handleSubmit = async (values: FormType) => {
    const { isNextMeetingSet, ...submitData } = values;
    await submitRoadmap({ variables: { input: submitData }, refetchQueries: ['getRoadmap', 'getCompletedRoadmapMilestones'] });
  };

  const initialValues: FormType = {
    roadmapId: roadmap.id,
    isNextMeetingSet: undefined,
    nextRoadmap: {
      plannedStartDate: new Date().toLocaleDateString()
    },
    reviewNotes: '',
    advisorReviewNotes: '',
    advisorPin: '',
    studentPin: ''
  };

  const initialStatus = {
    errors: [] as string[]
  };

  const enabledQuestionsFlags = (() => {
    const flags = siteCtx.site?.settings?.find(s => s.key === 'FINALIZE_RM_QUESTIONS_FLAGS');

    console.log(`flags ${flags?.value}`);

    return flags != null ? Number(flags.value) : null;
  })();

  console.log(`enabledQuestionsFlags ${enabledQuestionsFlags}`);

  const enabledQuestions: FinalizeRoadmapFormProps = {
    endDateEnabled: ((enabledQuestionsFlags ?? 0) & END_DATE_ENABLED) !== 0,
    userPinEnabled: userPinEnabled,
    nextMeetingEnabled: ((enabledQuestionsFlags ?? NEXT_MEETING_ENABLED) & NEXT_MEETING_ENABLED) !== 0,
    nextDateEnabled: ((enabledQuestionsFlags ?? NEXT_DATE_ENABLED) & NEXT_DATE_ENABLED) !== 0,
    reviewNotesEnabled: ((enabledQuestionsFlags ?? REVIEW_NOTES_ENABLED) & REVIEW_NOTES_ENABLED) !== 0
  };

  console.log(`enabledQuestions`, enabledQuestions);

  return (
    <Formik
      initialValues={initialValues}
      initialStatus={initialStatus}
      onSubmit={handleSubmit}
      validationSchema={finalizeRoadmapSchema(userPinEnabled, enabledQuestions.nextMeetingEnabled)}
    >
      <Dialog {...dlgProps} disableBackdropClick maxWidth="lg" fullWidth={true}>
        <DialogTitle onClose={dlgCtrl.setClose}>
          <LanguageString groupName="ROADMAP" resourceName="FINALIZE_ROADMAP" />
        </DialogTitle>
        <DialogContent dividers>
          {qry.called && qry.loading ? (
            <CircularProgress color="primary" />
          ) : qry.called && !qry.error ? (
            <LanguageString groupName="Roadmap" resourceName="SUBMIT_SUCCESS" defaultText="Submitted successfully" />
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Form>
                  <FinalizeRoadmapForm {...enabledQuestions} />
                </Form>
              </Grid>
              <Grid item xs={12}>
                {qry.error && <Alert severity="error">{qry.error.graphQLErrors.map(ge => ge.message).join(',')}</Alert>}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {qry.called && !qry.error ? (
            <Button onClick={dlgCtrl.setClose}>Close</Button>
          ) : (
            <Fragment>
              <Button onClick={dlgCtrl.setClose}>Cancel</Button>
              <SubmitBtn />
            </Fragment>
          )}
        </DialogActions>
      </Dialog>
    </Formik>
  );
};
